import { useCallback, useEffect, useMemo, useRef, useState } from "preact/hooks";
import type { MutableRef } from "preact/hooks";
import {
  submitSupportRequest,
  type ArticleFull,
  type WidgetConfig,
} from "./data";

// The in-panel "Contact support" form, replacing the old link out to the Zendesk
// help centre (support.validaide.com/hc/en-us/requests/new).
//
// It carries the same four fields that form did — reply-to address, subject,
// description, attachments — because screenshots are the whole reason a reader
// contacts support from inside the product, and the one thing a `mailto:` link
// can't carry. Three ways to attach one, in the order people actually reach for
// them: paste (Cmd/Ctrl+V straight from the screenshot tool), drop, or pick.
//
// Everything the reader typed survives a failed submit — and Esc, the back
// chevron and Collapse, all of which unmount this view: they are already
// annoyed enough to be writing to support, and re-typing it is the last thing
// they should have to do. The draft therefore lives in a ref the panel owns
// (`draft`), mirrored from local state on every change and read back on mount;
// only Cancel and a successful send clear it.

const MAX_SCREENSHOTS = 5;
const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;
const MAX_MESSAGE_CHARS = 5000;
const ALLOWED_TYPES = new Set(["image/png", "image/jpeg", "image/gif", "image/webp"]);

// Same deliberately-loose shape the server accepts: this is a Reply-To hint, not
// a credential, and a strict pattern rejects valid addresses more often than it
// catches a typo.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The reply-to address, remembered per browser so a reader who writes in twice
// doesn't retype it. Same defensive wrapping as the vote memory in App.tsx —
// localStorage throws outright in some embedding contexts.
const EMAIL_KEY = "skycell-kb-support-email";
function readEmail(): string {
  try {
    return localStorage.getItem(EMAIL_KEY) ?? "";
  } catch {
    return "";
  }
}
function writeEmail(email: string): void {
  try {
    localStorage.setItem(EMAIL_KEY, email);
  } catch {
    /* just isn't remembered in this browser */
  }
}

const PaperclipIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
    stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="m21.44 11.05-9.19 9.19a6 6 0 0 1-8.49-8.49l8.57-8.57A4 4 0 1 1 18 8.84l-8.59 8.57a2 2 0 0 1-2.83-2.83l8.49-8.48" />
  </svg>
);
const TrashIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
    stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" />
  </svg>
);
const SentIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
    stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

/** A picked screenshot plus the object URL its thumbnail is drawn from. */
export type Shot = { file: File; url: string };

/** What the reader has typed so far; outlives the form view (see above). */
export type SupportDraft = {
  email: string;
  subject: string;
  message: string;
  ccMe: boolean;
  shots: Shot[];
};

/**
 * Forget a draft, revoking the screenshot thumbnails' object URLs — those are a
 * document-lifetime leak until revoked, and this is the one place a draft's
 * shots definitively stop being shown.
 */
export function discardDraft(draft: MutableRef<SupportDraft | null>): void {
  for (const s of draft.current?.shots ?? []) URL.revokeObjectURL(s.url);
  draft.current = null;
}

const kb = (bytes: number): string =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

export default function SupportForm({
  config,
  article,
  draft,
  onDone,
}: {
  config: WidgetConfig;
  /** The article open when support was clicked — quoted into the request. */
  article: ArticleFull | null;
  /** The panel-owned draft this view edits; null = start fresh. */
  draft: MutableRef<SupportDraft | null>;
  /** Leave the form (the header's back chevron does the same). */
  onDone: () => void;
}) {
  const [email, setEmail] = useState(() => draft.current?.email ?? readEmail());
  const [subject, setSubject] = useState(() => draft.current?.subject ?? "");
  const [message, setMessage] = useState(() => draft.current?.message ?? "");
  // Off unless asked for: a copy of the request lands in the reader's inbox, so
  // it is theirs to opt into rather than ours to assume.
  const [ccMe, setCcMe] = useState(() => draft.current?.ccMe ?? false);
  const [shots, setShots] = useState<Shot[]>(() => draft.current?.shots ?? []);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once the reader has tried to submit, so the form doesn't scold anyone
  // for an empty field they simply haven't reached yet.
  const [touched, setTouched] = useState(false);
  const [dropping, setDropping] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // Mirror every change into the panel-owned draft, so leaving this view (Esc,
  // back, Collapse — all of which unmount it) keeps what was typed. Object URLs
  // are revoked when a shot is dropped (removeShot) or the draft is discarded
  // (discardDraft) — NOT on unmount, since the thumbnails come back with it.
  useEffect(() => {
    draft.current = { email, subject, message, ccMe, shots };
  }, [draft, email, subject, message, ccMe, shots]);

  // Cancel, or leaving the "sent" screen: the draft is done with.
  const finish = useCallback(() => {
    discardDraft(draft);
    onDone();
  }, [draft, onDone]);

  const addFiles = useCallback((incoming: FileList | File[] | null) => {
    const files = Array.from(incoming ?? []);
    if (files.length === 0) return;
    const rejected: string[] = [];
    setShots((prev) => {
      const next = [...prev];
      for (const file of files) {
        if (next.length >= MAX_SCREENSHOTS) {
          rejected.push(`only ${MAX_SCREENSHOTS} screenshots per request`);
          break;
        }
        if (!ALLOWED_TYPES.has(file.type)) {
          rejected.push(`${file.name || "that file"} isn't a PNG, JPEG, GIF or WebP image`);
          continue;
        }
        if (file.size > MAX_SCREENSHOT_BYTES) {
          rejected.push(`${file.name || "that image"} is larger than 10 MB`);
          continue;
        }
        next.push({ file, url: URL.createObjectURL(file) });
      }
      return next;
    });
    // One line, not a stack of them: the reader needs to know why nothing
    // appeared, not a per-file report.
    setError(rejected.length > 0 ? `Couldn't attach — ${rejected[0]}.` : null);
  }, []);

  const removeShot = useCallback((url: string) => {
    setShots((prev) => {
      const gone = prev.find((s) => s.url === url);
      if (gone) URL.revokeObjectURL(gone.url);
      return prev.filter((s) => s.url !== url);
    });
  }, []);

  // Paste is the shortest path from a screenshot tool to this form, so accept
  // images pasted anywhere in it — not just into a file field nobody aims at.
  const onPaste = useCallback(
    (e: ClipboardEvent) => {
      const items = Array.from(e.clipboardData?.items ?? []);
      const images = items
        .filter((i) => i.kind === "file" && i.type.startsWith("image/"))
        .map((i) => i.getAsFile())
        .filter((f): f is File => f !== null);
      if (images.length === 0) return; // plain text paste: leave it to the field
      e.preventDefault();
      addFiles(images);
    },
    [addFiles],
  );

  const invalid = useMemo(() => {
    if (!EMAIL_RE.test(email.trim())) return "Enter the email address we should reply to.";
    if (!subject.trim()) return "Add a short subject.";
    if (message.trim().length < 10) return "Describe the problem in a little more detail.";
    return null;
  }, [email, subject, message]);

  const submit = useCallback(() => {
    setTouched(true);
    if (invalid) {
      setError(invalid);
      return;
    }
    setError(null);
    setSending(true);
    const address = email.trim();
    submitSupportRequest(config, {
      email: address,
      subject: subject.trim(),
      message: message.trim(),
      ccMe,
      screenshots: shots.map((s) => s.file),
      articleSlug: article?.slug,
      articleTitle: article?.title,
    })
      .then(() => {
        writeEmail(address);
        setSent(true);
        // Spent: a second request starts clean, and Esc from the "sent" screen
        // must not resurrect the screenshots.
        setShots([]);
        setSubject("");
        setMessage("");
        setCcMe(false);
        discardDraft(draft);
      })
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Couldn't send your request.");
      })
      .finally(() => setSending(false));
  }, [config, invalid, email, subject, message, ccMe, shots, article, draft]);

  if (sent) {
    return (
      <div class="kb-body">
        <div class="kb-support-sent">
          <span class="kb-support-sent-glyph"><SentIcon /></span>
          <p class="kb-support-sent-title">Message sent</p>
          <p class="kb-support-sent-note">
            Customer Support has your request and will reply to {email.trim()}.
            {ccMe && " A copy is on its way to you."}
          </p>
          <button type="button" class="kb-support-submit" onClick={finish}>
            Back to the Knowledge Base
          </button>
        </div>
      </div>
    );
  }

  return (
    <div class="kb-body">
      <form
        class={`kb-support-form${dropping ? " dropping" : ""}`}
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        onPaste={onPaste}
        // Drag-and-drop onto the form. dragover must be prevented too, or the
        // browser navigates to the dropped file instead of handing it over.
        onDragOver={(e) => {
          e.preventDefault();
          setDropping(true);
        }}
        onDragLeave={() => setDropping(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDropping(false);
          addFiles(e.dataTransfer?.files ?? null);
        }}
      >
        <p class="kb-support-intro">
          Tell Customer Support what's going wrong. Screenshots help a lot — paste,
          drop or pick them below.
        </p>

        {article && (
          <p class="kb-support-context">
            About <strong>{article.title}</strong>
          </p>
        )}

        <label class="kb-field">
          <span class="kb-field-label">Your email address</span>
          <input
            type="email"
            required
            autocomplete="email"
            placeholder="you@company.com"
            value={email}
            onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
          />
        </label>

        <label class="kb-field">
          <span class="kb-field-label">Subject</span>
          <input
            type="text"
            required
            maxLength={200}
            placeholder="Short summary of the problem"
            value={subject}
            onInput={(e) => setSubject((e.target as HTMLInputElement).value)}
          />
        </label>

        <label class="kb-field">
          <span class="kb-field-label">Description</span>
          <textarea
            required
            rows={6}
            maxLength={MAX_MESSAGE_CHARS}
            placeholder="What were you doing, what did you expect, and what happened instead?"
            value={message}
            onInput={(e) => setMessage((e.target as HTMLTextAreaElement).value)}
          />
        </label>

        <label class="kb-check">
          <input
            type="checkbox"
            checked={ccMe}
            onChange={(e) => setCcMe((e.target as HTMLInputElement).checked)}
          />
          <span>Send me a copy of this request</span>
        </label>

        <div class="kb-field">
          <span class="kb-field-label">
            Screenshots <span class="kb-field-hint">optional — up to {MAX_SCREENSHOTS}, 10 MB each</span>
          </span>
          <button
            type="button"
            class="kb-attach"
            onClick={() => fileInput.current?.click()}
            disabled={shots.length >= MAX_SCREENSHOTS}
          >
            <PaperclipIcon />
            {shots.length >= MAX_SCREENSHOTS ? "Attachment limit reached" : "Add screenshots"}
          </button>
          <input
            ref={fileInput}
            class="kb-file-input"
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            multiple
            onChange={(e) => {
              const el = e.target as HTMLInputElement;
              addFiles(el.files);
              // Reset so picking the same file twice still fires a change.
              el.value = "";
            }}
          />
          {shots.length > 0 && (
            <ul class="kb-shots">
              {shots.map((s) => (
                <li key={s.url} class="kb-shot">
                  <img src={s.url} alt="" />
                  <span class="kb-shot-name">
                    {s.file.name || "screenshot"}
                    <span class="kb-shot-size">{kb(s.file.size)}</span>
                  </span>
                  <button
                    type="button"
                    class="kb-shot-remove"
                    aria-label={`Remove ${s.file.name || "screenshot"}`}
                    onClick={() => removeShot(s.url)}
                  >
                    <TrashIcon />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {error && <p class="kb-support-error" role="alert">{error}</p>}

        <div class="kb-support-actions">
          <button type="button" class="kb-support-cancel" onClick={finish} disabled={sending}>
            Cancel
          </button>
          <button
            type="submit"
            class="kb-support-submit"
            // Only disabled while in flight: a disabled submit button that never
            // says why is worse than one that submits and shows the reason.
            disabled={sending || (touched && Boolean(invalid))}
          >
            {sending ? "Sending…" : "Send to Customer Support"}
          </button>
        </div>
      </form>
    </div>
  );
}
